import React, { useState } from "react";
import { Send, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  TextField,
  TextArea,
  SelectField,
  RadioGroup,
} from "@/components/forms/FormFields";
import PhotoUpload from "@/components/forms/PhotoUpload";
import {
  submitSubmission,
  CATEGORY_OPTIONS,
  MEMBERSHIP_OPTIONS,
} from "@/api/submissions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = {
  bedrijfsnaam: "",
  contactpersoon: "",
  email: "",
  telefoon: "",
  adres: "",
  postcode: "",
  categorie: "",           // legacy: eerste gekozen categorie
  categories: [],          // multi-select
  membership: "",
  website: "",
  instagram: "",
  facebook: "",
  omschrijving_nl: "",
  openingstijden: "",
  foto_url: "",
};

const SuccessCard = ({ onReset }) => (
  <div
    data-testid="submission-success"
    className="text-center bg-white rounded-2xl border border-[#6BC0E8]/40 p-8 sm:p-12 shadow-sm"
  >
    <div className="w-16 h-16 rounded-full bg-[#6BC0E8]/20 mx-auto flex items-center justify-center mb-5">
      <CheckCircle2 className="w-8 h-8 text-[#2E9CD6]" />
    </div>
    <h3 className="text-2xl sm:text-3xl font-bold mb-3">Bedankt!</h3>
    <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed max-w-md mx-auto mb-6">
      We nemen je gegevens door en zetten ze zo snel mogelijk op Lemmer Leeft.
    </p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <button
        type="button"
        onClick={onReset}
        data-testid="submission-new"
        className="ll-btn-outline"
      >
        Nog een inzending doen
      </button>
      <Link to="/bedrijvengids" className="ll-btn-primary">
        Terug naar de gids
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

/**
 * Herbruikbaar submission-formulier.
 * mode: "nieuw" | "aanvulling"
 * lockedListing: bij "aanvulling" het gekozen bedrijf (levert linked_slug + voor-invulling)
 */
export const SubmissionForm = ({ mode = "nieuw", lockedListing = null, editToken = "" }) => {
  const isAanvulling = mode === "aanvulling";
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    bedrijfsnaam: lockedListing?.name || "",
    adres: lockedListing?.address || "",
    postcode: lockedListing?.postcode || "",
    telefoon: lockedListing?.phone || "",
    email: lockedListing?.email || "",
    website: lockedListing?.website || "",
    omschrijving_nl: lockedListing?.description_nl || "",
    openingstijden: lockedListing?.opening_hours || "",
    categorie: lockedListing?.category || "",
    categories: lockedListing?.categories?.length
      ? [...lockedListing.categories]
      : (lockedListing?.category ? [lockedListing.category] : []),
  }));
  const [photoPreview, setPhotoPreview] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);

  // Absolute URL van de huidige listing-foto (voor "Huidige foto"-preview bij aanvullingen).
  // Relatieve /api/uploads/... path prefixen met REACT_APP_BACKEND_URL.
  const existingImage = React.useMemo(() => {
    const raw = lockedListing?.image || "";
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${raw}`;
  }, [lockedListing?.image]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const toggleCategory = (slug) => {
    setForm((f) => {
      const next = f.categories.includes(slug)
        ? f.categories.filter((c) => c !== slug)
        : [...f.categories, slug];
      return { ...f, categories: next, categorie: next[0] || "" };
    });
    if (errors.categorie) setErrors((e) => ({ ...e, categorie: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.bedrijfsnaam.trim()) e.bedrijfsnaam = "Vul de bedrijfsnaam in.";
    if (!form.contactpersoon.trim())
      e.contactpersoon = "Vul een contactpersoon in.";
    if (!form.email.trim()) e.email = "Vul een e-mailadres in.";
    else if (!EMAIL_RE.test(form.email))
      e.email = "Vul een geldig e-mailadres in.";
    if (!isAanvulling) {
      if (!form.categories?.length) e.categorie = "Kies minstens één categorie.";
      if (!form.membership) e.membership = "Maak een keuze.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) {
      // scroll naar eerste error
      const first = document.querySelector('[aria-invalid="true"], .border-\\[\\#CE1B2A\\]');
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        type: mode,
        linked_slug: isAanvulling ? lockedListing?.slug || null : null,
        ...form,
        // Zorg dat de legacy 'categorie' de eerste is uit de multi-select
        categorie: form.categories?.[0] || form.categorie || "",
        // Alleen bij aanvulling: magic-link-token dat de wijziging autoriseert.
        edit_token: isAanvulling ? editToken : "",
      };
      // clean empty strings that pydantic tolerates
      await submitSubmission(payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(
        Array.isArray(detail)
          ? "Controleer de gemarkeerde velden."
          : detail ||
              "Er ging iets mis met verzenden. Probeer het zo nog eens."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({
      ...emptyForm,
      bedrijfsnaam: lockedListing?.name || "",
      adres: lockedListing?.address || "",
      postcode: lockedListing?.postcode || "",
      telefoon: lockedListing?.phone || "",
      email: lockedListing?.email || "",
      website: lockedListing?.website || "",
      categorie: lockedListing?.category || "",
    });
    setPhotoPreview("");
    setErrors({});
    setSuccess(false);
    setServerError("");
  };

  if (success) return <SuccessCard onReset={handleReset} />;

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`form-${mode}`}
      noValidate
      className="space-y-5"
    >
      {/* Bedrijfsinfo */}
      {isAanvulling && (
        <div
          data-testid="aanvulling-note"
          className="p-3 rounded-lg bg-[#EAF6FC] border border-[#6BC0E8] text-[#173A5E] text-sm font-[Inter]"
        >
          <strong>Alle velden zijn vooraf gevuld met je huidige gegevens.</strong>{" "}
          Wijzig alleen wat nieuw of veranderd is — velden die je leeg laat blijven op de huidige waarde staan.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField
          id="bedrijfsnaam"
          label="Bedrijfsnaam"
          required
          value={form.bedrijfsnaam}
          onChange={(e) => set("bedrijfsnaam", e.target.value)}
          error={errors.bedrijfsnaam}
          placeholder="Naam van je bedrijf"
        />
        <TextField
          id="contactpersoon"
          label="Contactpersoon"
          required
          value={form.contactpersoon}
          onChange={(e) => set("contactpersoon", e.target.value)}
          error={errors.contactpersoon}
          placeholder="Jouw naam"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField
          id="email"
          label="E-mailadres"
          required
          type="email"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          error={errors.email}
          placeholder="jij@bedrijf.nl"
          disabled={isAanvulling}
          title={isAanvulling ? "Het e-mailadres is het verificatie-anker en kan hier niet gewijzigd worden." : undefined}
        />
        <TextField
          id="telefoon"
          label="Telefoon"
          value={form.telefoon}
          onChange={(e) => set("telefoon", e.target.value)}
          placeholder="0514 12 34 56"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <TextField
            id="adres"
            label="Adres"
            value={form.adres}
            onChange={(e) => set("adres", e.target.value)}
            placeholder="Straat en huisnummer"
          />
        </div>
        <TextField
          id="postcode"
          label="Postcode"
          value={form.postcode}
          onChange={(e) => set("postcode", e.target.value)}
          placeholder="8531 XX"
        />
      </div>

      {/* Categorieën — óók bij aanvulling zichtbaar zodat ondernemer categorieën kan aanvullen. */}
      <div className="grid gap-1.5">
        <label className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#173A5E]">
          Categorieën {!isAanvulling && <span className="text-[#CE1B2A]">*</span>}
          <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-[#8AA3B8]">
            (meerkeuze — vink alles aan wat past)
          </span>
        </label>
        <div
          data-testid="submission-categories"
          className={`grid grid-cols-1 sm:grid-cols-2 gap-2 p-4 rounded-xl border ${
            errors.categorie ? "border-[#CE1B2A] bg-[#FFF6F7]" : "border-[#E4EEF5] bg-[#F9FBFD]"
          }`}
        >
          {CATEGORY_OPTIONS.map((o) => (
            <label
              key={o.value}
              className="inline-flex items-center gap-2 text-sm cursor-pointer text-[#173A5E] hover:text-[#0F2A46]"
            >
              <input
                type="checkbox"
                checked={form.categories.includes(o.value)}
                onChange={() => toggleCategory(o.value)}
                data-testid={`submission-cat-${o.value}`}
                className="w-4 h-4 accent-[#2E9CD6]"
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
        {errors.categorie && (
          <p className="text-xs text-[#CE1B2A] font-[Poppins] font-semibold">{errors.categorie}</p>
        )}
      </div>

      {!isAanvulling && (
        <RadioGroup
          id="membership"
          label="Lid of donateur"
          required
          options={MEMBERSHIP_OPTIONS}
          value={form.membership}
          onChange={(v) => set("membership", v)}
          error={errors.membership}
        />
      )}

      {/* Online */}
      <div className="pt-2">
        <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-3">
          Online vindbaarheid
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField
            id="website"
            label="Website"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            placeholder="www.bedrijf.nl"
          />
          <TextField
            id="instagram"
            label="Instagram"
            value={form.instagram}
            onChange={(e) => set("instagram", e.target.value)}
            placeholder="@bedrijf"
          />
          <TextField
            id="facebook"
            label="Facebook"
            value={form.facebook}
            onChange={(e) => set("facebook", e.target.value)}
            placeholder="facebook.com/bedrijf"
          />
        </div>
      </div>

      {/* Omschrijving + openingstijden */}
      <TextArea
        id="omschrijving_nl"
        label="Korte omschrijving"
        value={form.omschrijving_nl}
        onChange={(e) => set("omschrijving_nl", e.target.value)}
        placeholder="Vertel in een paar zinnen wat bezoekers bij je vinden."
      />

      <TextArea
        id="openingstijden"
        label="Openingstijden"
        value={form.openingstijden}
        onChange={(e) => set("openingstijden", e.target.value)}
        placeholder={"Ma-vr 09:00 - 17:30\nZa 09:00 - 17:00\nZo gesloten"}
      />

      {/* Foto */}
      <PhotoUpload
        value={form.foto_url}
        absoluteUrl={photoPreview}
        existingImage={isAanvulling ? existingImage : ""}
        onUploaded={(url, abs) => {
          set("foto_url", url);
          setPhotoPreview(abs || "");
        }}
      />

      {serverError && (
        <div
          data-testid="server-error"
          className="p-4 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] text-sm font-[Inter]"
        >
          {serverError}
        </div>
      )}

      <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <p className="text-xs text-[#5A7594] font-[Inter] max-w-md">
          Je gegevens worden alleen gebruikt voor je vermelding op Lemmer Leeft
          en gaan niet door aan derden.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-btn"
          className="ll-btn-primary disabled:opacity-60 disabled:cursor-wait shrink-0"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {isAanvulling ? "Aanvullingen versturen" : "Aanmelding versturen"}
        </button>
      </div>
    </form>
  );
};

export default SubmissionForm;
